import { useEffect } from 'react';

import { Avatar, Box, Button, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
    Link,
    Outlet,
    createFileRoute,
    redirect,
    useLocation,
    useMatches,
    useNavigate,
} from '@tanstack/react-router';

import SageLogo from 'src/assets/images/sage-logo.svg?react';
import { useAuth0Context } from 'src/auth/auth0';
import MainNav from 'src/components/MainNav';
import useResponsive from 'src/hooks/useResponsive';
import { useTeamMemberData } from 'src/hooks/useTeamMemberData';
import {
    setCareRecipientDetails,
    setOnboardingUserDetails,
} from 'src/store/slices/onboardingSlice';
import { useAppDispatch } from 'src/store/store';

export const Route = createFileRoute('/(app)')({
    beforeLoad: ({ context }) => {
        const { isAuthenticated, isLoading } = context.auth ?? {};

        if (!isLoading && !isAuthenticated) {
            throw redirect({ to: '/login', replace: true });
        }
    },
    component: ProtectedLayout,
});

function ProtectedLayout() {
    const location = useLocation();
    const matches = useMatches();
    const navigate = useNavigate();
    const { spacing, palette } = useTheme();
    const dispatch = useAppDispatch();

    const { isLoading: isLoadingAuth } = useAuth0Context();

    // Use the new centralized hook for team member data
    const {
        currentTeam,
        careRecipient,
        isLoading: isLoadingTeamData,
        hasOnboarded,
        currentUser,
    } = useTeamMemberData();

    const shouldUseChildRouteLayout =
        matches?.some?.((match) => match.staticData?.useChildRouteLayout) ?? false;

    const { isMobile, isDesktop, isTablet } = useResponsive();

    const accountActive = location.pathname === '/account';
    const isOnboarding = location.pathname.startsWith('/onboarding');

    // Set onboarding data when team member and team are available
    useEffect(() => {
        if (currentUser && currentTeam) {
            dispatch(setOnboardingUserDetails(currentUser));

            if (careRecipient) {
                dispatch(setCareRecipientDetails(careRecipient));
            }

            // Redirect to /onboarding if team member is not onboarded or has not accepted their invitation
            if (!isOnboarding && !hasOnboarded) {
                navigate({ to: '/onboarding/confirm-info', replace: true });
            }
        }
    }, [currentTeam, currentUser, careRecipient, navigate, dispatch, isOnboarding, hasOnboarded]);

    const isLoading = isLoadingAuth || isLoadingTeamData;

    // This is the layout for the main app (authenticated)
    return (
        <Box sx={{ height: '100vh', backgroundColor: 'grey.300' }}>
            {!isLoading && (isMobile || shouldUseChildRouteLayout) && (
                // Mobile layout - only main content
                <Outlet />
            )}
            {!isLoading && (isTablet || isDesktop) && !shouldUseChildRouteLayout && (
                // Tablet & Desktop layout
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: isTablet ? `${spacing(35)} 1fr` : `${spacing(47)} 1fr`,
                        gap: 2,
                        height: '100vh',
                        overflow: 'hidden',
                    }}
                >
                    {/* Left Navigation section (Tablet & Desktop) */}
                    <Stack
                        sx={{
                            backgroundColor: 'common.white',
                            pt: 5,
                            pb: 10,
                            justifyContent: 'space-between',
                            height: '100vh',
                        }}
                    >
                        <Stack sx={{ width: '100%', gap: 4 }}>
                            <Link to="/home" style={{ display: 'flex', justifyContent: 'center' }}>
                                <SageLogo
                                    width={120}
                                    aria-label="Sage Logo"
                                    style={{ alignSelf: 'center' }}
                                />
                            </Link>
                            <MainNav />
                        </Stack>

                        {/* Account button */}
                        <Box sx={{ pl: 5 }}>
                            <Button
                                onClick={() => {
                                    if (currentUser?.teamMemberRid) {
                                        navigate({
                                            to: '/care/team/member/$teamMemberRid',
                                            params: { teamMemberRid: currentUser?.teamMemberRid },
                                        });
                                    }
                                }}
                                aria-label="Account"
                                fullWidth
                                sx={{
                                    backgroundColor: accountActive ? 'grey.200' : 'transparent',
                                    padding: 2.5,
                                    minWidth: 'inherit',
                                    justifyContent: 'flex-start',
                                    gap: 2.5,
                                    '&:hover': {
                                        backgroundColor: 'grey.200',
                                    },
                                    borderRadius: `${spacing(2)} 0 0 ${spacing(2)}`,
                                    '&:before': {
                                        content: '""',
                                        position: 'absolute',
                                        top: 0,
                                        right: 0,
                                        width: spacing(0.5),
                                        height: '100%',
                                        backgroundColor: accountActive
                                            ? palette?.purple?.[400]
                                            : 'transparent',
                                    },
                                }}
                            >
                                <Avatar />
                                <Typography variant="body1" sx={{ color: 'gray.900' }}>
                                    Account
                                </Typography>
                            </Button>
                        </Box>
                    </Stack>
                    {/* Main content outlet */}
                    <Box
                        component="main"
                        sx={{
                            p: isTablet ? 5 : 10,
                            overflow: 'auto',
                            height: '100vh',
                        }}
                    >
                        <Outlet />
                    </Box>
                </Box>
            )}
        </Box>
    );
}
